import {
  ChatCustomFieldValueRaw,
  ChatLogActivityAction,
  ChatProposalAction,
} from "@mapmycustomers/shared/types/chat/ChatContent";
import { CustomFieldValueType } from "@mapmycustomers/shared/types/customField/CustomFieldValueType";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";

import ChatCustomFieldValue from "@app/types/chat/ChatCustomFieldValue";
import activityFieldModel from "@app/util/fieldModel/ActivityFieldModel";
import { isCustomField } from "@app/util/fieldModel/impl/assert";
import loggingService from "@app/util/logging";
import { parseApiDate } from "@app/util/parsers";

// A stored draft outlives a reload and an app version change, so its entries are re-checked rather
// than trusted. Lives here beside the drafts it guards; logActivityFormValues and proposalApply both
// read stored drafts through draftCustomFieldsOf below.
//
// A draft written before the esKey-only split still carries customFieldId; the extra key is ignored
// rather than rejected, so an editor left open across a deploy still opens on the rep's own work.
export const isChatCustomFieldValue = (candidate: unknown): candidate is ChatCustomFieldValue => {
  const value = candidate as null | Partial<ChatCustomFieldValue>;
  return (
    typeof value?.esKey === "string" &&
    // Not Array.isArray: a single-option value is a bare number.
    value.value !== undefined
  );
};

export const draftCustomFieldsOf = (draft: Record<string, unknown>): ChatCustomFieldValue[] =>
  Array.isArray(draft.customFields) ? draft.customFields.filter(isChatCustomFieldValue) : [];

// Puts the platform's own key back on. A draft is addressed by esKey; customFieldId is what the
// apply endpoint writes by, and it comes off the resolved definition rather than being carried
// through the draft.
//
// A field the org deleted since the proposal resolves to nothing and is dropped — there is no id to
// write it under, so the rep's edit to it was never going to land. Callers decide what an empty
// result means: for a log_activity draft [] is a real instruction ("the rep cleared them"), for a
// record_update it would wipe the proposed set and must be omitted instead.
export const toRawCustomFieldValues = (
  fieldModel: IFieldModel,
  values: ChatCustomFieldValue[]
): ChatCustomFieldValueRaw[] =>
  values.flatMap(({ esKey, value }) => {
    const field = fieldModel.getByName(esKey);
    if (!field || !isCustomField(field)) {
      loggingService.warning("Dropping a drafted custom field the org no longer defines", {
        esKey,
      });
      return [];
    }
    return [{ customFieldId: field.customFieldData.id, esKey, value }];
  });

// CustomFieldValueType is array-wrapped for some data types and bare for others, so anything that
// counts, compares or indexes a value's elements goes through here rather than assuming an array.
export const customFieldValueItems = (value: CustomFieldValueType): unknown[] => {
  if (value === undefined || value === null) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
};

// The draft's own shape is the wire's, not the activity form's, so it lives here beside the
// serializer that ships it. The form-values bridge stays with the editor that renders one.
export interface LogActivityDraft {
  crmActivityTypeId: number;
  customFields?: ChatCustomFieldValue[];
  endAt: string;
  name: string;
  note: string;
  startAt: string;
}

// Absent optional wire values become "" so the draft's shape is stable and isLogActivityDraftEqual
// never has to compare undefined against "".
export const draftFromAction = (action: ChatLogActivityAction): LogActivityDraft => ({
  crmActivityTypeId: action.record.crmActivityType.id,
  // Mapped rather than passed through: the wire entry carries customFieldId too, and a draft that
  // quietly stored it would drift from the type that says it does not.
  customFields: (action.record.customFields ?? []).map(({ esKey, value }) => ({ esKey, value })),
  endAt: action.record.endAt ?? "",
  name: action.record.name,
  note: action.record.note ?? "",
  startAt: action.record.startAt ?? "",
});

// The same instant has many spellings; treat two empty strings as equal without asking a parser to
// turn "" into a comparable timestamp.
//
// parseApiDate, not parseApiDateAsNumber: that variant reports an unparseable string as 0, which
// would make an absent value and a real instant compare equal. NaN never equals itself, which is
// exactly the "different" this needs.
export const sameInstant = (left: string, right: string): boolean =>
  left === right || parseApiDate(left).getTime() === parseApiDate(right).getTime();

// Normalized the way the backend's own dedupe is: entries by id, then a sorted copy of each value
// array. Order means nothing either way — the form rebuilds the array from a Record keyed by
// definition id, and a multi-select re-picked in another order is the same selection.
// A single-option value arrives bare rather than array-wrapped, so each value is normalized to an
// array before it is sorted — spreading a bare number would throw.
const sortedValue = (value: CustomFieldValueType): unknown[] =>
  Array.isArray(value) ? [...value].sort() : [value];

const customFieldsKey = (values: ChatCustomFieldValue[]): string =>
  JSON.stringify(
    [...values]
      .sort((left, right) => left.esKey.localeCompare(right.esKey))
      .map(({ esKey, value }) => [esKey, sortedValue(value)])
  );

export const isLogActivityDraftEqual = (
  action: ChatProposalAction,
  draft: Record<string, unknown>
): boolean => {
  if (action.type !== "log_activity") {
    return false;
  }
  if (
    customFieldsKey(draftCustomFieldsOf(draft)) !==
    customFieldsKey(action.record.customFields ?? [])
  ) {
    return false;
  }
  const draftStartAt = typeof draft.startAt === "string" ? draft.startAt : "";
  // endAt is compared like startAt rather than left out: a rep who moved only the end time would
  // otherwise produce a draft that reads as unchanged, and the edit would be discarded here — long
  // before the wire could carry it.
  const draftEndAt = typeof draft.endAt === "string" ? draft.endAt : "";
  return (
    draft.crmActivityTypeId === action.record.crmActivityType.id &&
    draft.name === action.record.name &&
    draft.note === (action.record.note ?? "") &&
    sameInstant(draftStartAt, action.record.startAt ?? "") &&
    sameInstant(draftEndAt, action.record.endAt ?? "")
  );
};

// The sparse finalValues for an accepted log_activity, or undefined when the rep changed nothing.
export const toLogActivityFinalValues = (
  action: ChatLogActivityAction,
  draft: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => {
  if (!draft) {
    return undefined;
  }
  const proposed = draftFromAction(action);
  const finalValues: Record<string, unknown> = {};

  const crmActivityTypeId =
    typeof draft.crmActivityTypeId === "number" ? draft.crmActivityTypeId : undefined;
  if (crmActivityTypeId !== undefined && crmActivityTypeId !== proposed.crmActivityTypeId) {
    finalValues.crmActivityTypeId = crmActivityTypeId;
  }
  const name = typeof draft.name === "string" ? draft.name : undefined;
  if (name !== undefined && name !== proposed.name) {
    finalValues.name = name;
  }
  // note is the one key whose empty string is meaningful: the platform deletes the note when it
  // receives "", so an empty draft note must be sent rather than treated as "unchanged".
  const note = typeof draft.note === "string" ? draft.note : undefined;
  if (note !== undefined && note !== proposed.note) {
    finalValues.note = note;
  }
  // Same instant, different spelling: draftFromFormValues emits Z-suffixed UTC, the wire keeps its
  // original offset. String equality would call every save a change.
  const startAt = typeof draft.startAt === "string" ? draft.startAt : undefined;
  // An empty startAt is left to the platform's loud "invalid" rejection: the editor disables Save
  // while it is empty, so one arriving here is a bug, not a case to silently absorb.
  if (startAt !== undefined && !sameInstant(startAt, proposed.startAt)) {
    finalValues.startAt = startAt;
  }
  // "" rides when the rep cleared an end time the agent proposed, and is withheld when neither ever
  // set one — same rule the email's sendAt follows, since both are optional instants.
  const endAt = typeof draft.endAt === "string" ? draft.endAt : undefined;
  if (endAt !== undefined && !sameInstant(endAt, proposed.endAt)) {
    finalValues.endAt = endAt;
  }
  // Sent whenever the draft has the key, [] included — unlike the four keys above, which are sent
  // only when they differ. The backend reads an absent key as "keep the agent's set"
  // (customFieldValueOverride ?? finalRecord.customFields), so a cleared set must say [] out loud
  // or the values the rep deleted get written anyway. Omitted only for a draft stored before this
  // key existed, where "keep the agent's set" is the right reading.
  if (Array.isArray(draft.customFields)) {
    // Through toRawCustomFieldValues, not straight from the draft: the draft is addressed by esKey
    // and the apply endpoint writes by customFieldId. [] survives on purpose here — for this action
    // an empty array IS the instruction, unlike on a record_update.
    finalValues.customFields = toRawCustomFieldValues(
      activityFieldModel,
      draftCustomFieldsOf(draft)
    );
  }

  return Object.keys(finalValues).length > 0 ? finalValues : undefined;
};
